/* PRE-PILOT F8 — Kit Onboarding Pilote : l'utilisateur donne le minimum,
   NOAM part enquêter (Deep Organization Discovery asynchrone), puis montre
   ce qu'il SAIT / SUPPOSE / IGNORE. Adaptive Complexity : pas toute la
   puissance d'un coup — l'essentiel, puis drill-down. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Compass, Eye, Loader2, Play, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { fmtError, notify } from "@/lib/notify";

const PROFILES = [
  ["fast", "FAST — compréhension initiale (~1 min)"],
  ["standard", "STANDARD — onboarding normal"],
  ["deep", "DEEP — constitution approfondie"],
];

export default function OnboardingPilote() {
  const [me, setMe] = useState(null);
  const [status, setStatus] = useState(null);
  const [graph, setGraph] = useState(null);
  const [profile, setProfile] = useState("standard");
  const [launching, setLaunching] = useState(false);

  const load = () => {
    api.get("/workspace/me").then((r) => setMe(r.data)).catch(() => {});
    api.get("/workspace/world-watch/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/workspace/org-model/graph").then((r) => setGraph(r.data.stats)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (status?.task?.status !== "running") return;
    const iv = setInterval(() => api.get("/workspace/world-watch/status").then((r) => setStatus(r.data)).catch(() => {}), 4000);
    return () => clearInterval(iv);
  }, [status?.task?.status]);

  const launch = async () => {
    setLaunching(true);
    try {
      await api.post(`/workspace/world-watch/run?profile=${profile}`);
      notify.success("Deep Discovery lancé — vous pouvez continuer à utiliser NOAM pendant qu'il enquête.");
      load();
    } catch (e) { notify.error(fmtError(e)); }
    setLaunching(false);
  };

  const p = status?.task?.metadata?.progress;
  const running = status?.task?.status === "running";
  const run = status?.last_run;
  return (
    <div className="max-w-3xl space-y-5" data-testid="onboarding-pilote">
      <div>
        <h1 className="text-2xl font-semibold text-white">Onboarding pilote</h1>
        <p className="text-sm text-zinc-400 mt-1">Donnez le minimum — NOAM construit, puis cherche lui-même ce qu'il peut raisonnablement apprendre. Il ne demandera confirmation que lorsque cela crée de la valeur.</p>
      </div>

      <div className="noam-card p-4 space-y-1" data-testid="onboarding-step-org">
        <div className="text-[10px] uppercase tracking-widest text-[#378ADD]">1 · Organisation & Systems</div>
        <div className="text-sm text-white">{me?.organisation?.name || "…"} <span className="text-zinc-500">· {me?.organisation?.sector || "secteur non renseigné"}</span></div>
        <div className="text-xs text-zinc-400">Activez uniquement les Systems pertinents (profondeur progressive) dans <Link className="text-[#378ADD] hover:underline" to="/app/settings">Réglages → Systems</Link>.</div>
      </div>

      <div className="noam-card p-4 space-y-1" data-testid="onboarding-step-sources">
        <div className="text-[10px] uppercase tracking-widest text-[#378ADD]">2 · Premières sources</div>
        <div className="text-xs text-zinc-400">
          Déposez ce que vous avez — NOAM extrait tout seul (asynchrone, jamais bloquant) :
          <Link className="text-[#378ADD] hover:underline ml-1" to="/app/documents">Documents</Link> ·
          <Link className="text-[#378ADD] hover:underline ml-1" to="/app/gouvernance">Intake gouvernance</Link> ·
          import tabulaire (rejets ligne par ligne, jamais silencieux).
        </div>
      </div>

      <div className="noam-card p-4 space-y-3" data-testid="onboarding-step-discovery">
        <div className="text-[10px] uppercase tracking-widest text-[#378ADD] flex items-center gap-1.5"><Compass className="w-3.5 h-3.5" /> 3 · Deep Organization Discovery</div>
        <div className="text-xs text-zinc-400">Le graphe détermine ce que NOAM regarde : fournisseurs, contreparties, actifs, géographies. Arrêt automatique quand le gain marginal d'information devient faible.</div>
        <div className="flex items-center gap-2">
          <select value={profile} onChange={(e) => setProfile(e.target.value)} data-testid="discovery-profile-select"
            className="bg-black/30 border border-white/10 rounded px-2 py-1.5 text-xs text-zinc-200">
            {PROFILES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button onClick={launch} disabled={launching || running} data-testid="discovery-launch-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-[#378ADD] text-black text-xs font-semibold disabled:opacity-50">
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
            {running ? "Discovery en cours…" : "Lancer la découverte"}
          </button>
        </div>
        {(p || run) && (
          <div className="grid grid-cols-3 gap-2 text-center" data-testid="discovery-progress">
            {[["Sources examinées", (p || run)?.sources_consulted], ["Preuves utiles uniques", (p || run)?.unique_useful],
              ["Redondantes (écartées)", (p || run)?.redundant], ["Entités couvertes", (p || run)?.entities_covered],
              ["Signaux monde → org", (p || run)?.signals], ["Observations totales", status?.observations_total]].map(([l, v]) => (
              <div key={l} className="rounded border border-white/5 bg-white/[0.02] py-1.5">
                <div className="text-sm font-semibold text-white">{v ?? "—"}</div>
                <div className="text-[9px] text-zinc-500">{l}</div>
              </div>
            ))}
          </div>
        )}
        {run && !running && (
          <div className="text-[10px] text-zinc-500">Dernier run : profil {run.profile} · {run.machine_time_s}s machine · 0s humain bloqué · arrêt : {run.stopped_reason === "marginal_gain_low" ? "gain marginal faible" : "budget atteint"} · {run.human_confirmations_required} confirmation(s) humaine(s) demandée(s)</div>
        )}
      </div>

      <div className="noam-card p-4 space-y-2" data-testid="onboarding-step-knowledge">
        <div className="text-[10px] uppercase tracking-widest text-[#378ADD] flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> 4 · Ce que NOAM sait / suppose / ignore</div>
        <div className="grid grid-cols-3 gap-2 text-center">
          {[["SAIT (déclaré)", graph?.declared_edges, "text-emerald-300"],
            ["SUPPOSE (inféré)", graph?.inferred_edges, "text-amber-300"],
            ["IGNORE (à confirmer)", (graph?.unresolved_owners ?? 0) + (graph?.orphan_relations ?? 0), "text-red-300"]].map(([l, v, c]) => (
            <div key={l} className="rounded border border-white/5 bg-white/[0.02] py-2">
              <div className={`text-lg font-semibold ${c}`}>{v ?? "—"}</div>
              <div className="text-[9px] text-zinc-500">{l}</div>
            </div>
          ))}
        </div>
        <Link to="/app/org-model" className="text-xs text-[#378ADD] hover:underline">Explorer l'organisation vivante →</Link>
      </div>

      <div className="noam-card p-4 flex items-center gap-3" data-testid="onboarding-step-today">
        <Sparkles className="w-5 h-5 text-[#EF9F27]" />
        <div className="flex-1">
          <div className="text-sm text-white">5 · Première information utile</div>
          <div className="text-xs text-zinc-400">« Aujourd'hui » montre ce qui demande votre attention, pourquoi, et ce que NOAM recommande — votre première divergence ou décision chiffrée part de là.</div>
        </div>
        <Link to="/app/aujourdhui" data-testid="onboarding-goto-today"
          className="px-3 py-1.5 rounded bg-[#EF9F27] text-black text-xs font-semibold">Ouvrir Aujourd'hui</Link>
      </div>
    </div>
  );
}
